// W-0821 — Grid principal de boletos elegíveis.
// - Paginação server-side pageSize=50 (Gate 1B Q-08).
// - Multi-seleção persistente entre páginas (Set de codigo_boleto).
// - Sort local na página atual (LICOES §1.7 — sorting padrão de CRUD).
// - key={item.codigo_boleto} estável (LICOES §1.5).

import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Form,
  Pagination,
  Badge,
  Button,
} from 'react-bootstrap';
import { FaSort, FaSortUp, FaSortDown, FaUserPlus, FaTimes } from 'react-icons/fa';
import { formatarBRL } from '../helpers';

const thStyle = { cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };

const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'pt-BR', {
    numeric: true,
    sensitivity: 'base',
  });
};

const Cabecalho = ({ campo, label, sort, dir, onSort }) => {
  const ativo = sort === campo;
  const Icone = !ativo ? FaSort : dir === 'asc' ? FaSortUp : FaSortDown;
  return (
    <th style={thStyle} onClick={() => onSort(campo)}>
      {label} <Icone size={10} />
    </th>
  );
};

const renderizarPaginacao = (pagina, totalPaginas, onMudarPagina) => {
  if (totalPaginas <= 1) return null;
  const max = 5;
  const inicio = Math.max(1, pagina - Math.floor(max / 2));
  const fim = Math.min(totalPaginas, inicio + max - 1);
  const itens = [];
  itens.push(
    <Pagination.First
      key="first"
      disabled={pagina === 1}
      onClick={() => onMudarPagina(1)}
    />
  );
  itens.push(
    <Pagination.Prev
      key="prev"
      disabled={pagina === 1}
      onClick={() => onMudarPagina(pagina - 1)}
    />
  );
  if (inicio > 1) {
    itens.push(<Pagination.Ellipsis key="el-left" disabled />);
  }
  for (let i = inicio; i <= fim; i += 1) {
    itens.push(
      <Pagination.Item
        key={`pg-${i}`}
        active={i === pagina}
        onClick={() => onMudarPagina(i)}
      >
        {i}
      </Pagination.Item>
    );
  }
  if (fim < totalPaginas) {
    itens.push(<Pagination.Ellipsis key="el-right" disabled />);
  }
  itens.push(
    <Pagination.Next
      key="next"
      disabled={pagina === totalPaginas}
      onClick={() => onMudarPagina(pagina + 1)}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      disabled={pagina === totalPaginas}
      onClick={() => onMudarPagina(totalPaginas)}
    />
  );
  return itens;
};

const GridBoletos = ({
  boletos,
  total,
  pagina,
  tamanhoPagina,
  selecionados,
  atribuicoes,
  onToggleSelecao,
  onTogglePagina,
  onMudarPagina,
  onAbrirModalAtribuir,
  onRemoverAtribuicao,
  cobradoresMap, // map codigo -> nome (para mostrar cobrador atribuído inline)
}) => {
  const [sort, setSort] = useState('ordem');
  const [dir, setDir] = useState('asc');

  const trocarSort = (campo) => {
    if (campo === sort) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(campo);
      setDir('asc');
    }
  };

  const boletosOrdenados = useMemo(() => {
    const arr = [...boletos];
    arr.sort((a, b) => {
      const cmp = compararValores(a[sort], b[sort]);
      return dir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [boletos, sort, dir]);

  const codigosPagina = boletos.map((b) => b.codigo_boleto);
  const todosMarcados =
    codigosPagina.length > 0 &&
    codigosPagina.every((c) => selecionados.includes(c));
  const algumMarcado =
    codigosPagina.some((c) => selecionados.includes(c)) && !todosMarcados;

  const totalPaginas = Math.max(1, Math.ceil(total / tamanhoPagina));
  const inicio = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const fim = Math.min(pagina * tamanhoPagina, total);

  return (
    <Card className="mb-3">
      <Card.Header className="bg-light d-flex justify-content-between align-items-center flex-wrap">
        <span>
          <strong>Boletos elegíveis</strong>{' '}
          <Badge bg="info" className="ms-1">
            {selecionados.length} selecionado(s)
          </Badge>
        </span>
        <small className="text-muted">
          {total === 0
            ? 'Nenhum registro'
            : `Mostrando ${inicio} até ${fim} de ${total} registros`}
        </small>
      </Card.Header>
      <Card.Body className="p-0">
        <div className="table-responsive">
          <Table striped bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <Form.Check
                    type="checkbox"
                    aria-label="Selecionar página"
                    ref={(el) => {
                      if (el) el.indeterminate = algumMarcado;
                    }}
                    checked={todosMarcados}
                    onChange={(e) =>
                      onTogglePagina(codigosPagina, e.target.checked)
                    }
                  />
                </th>
                <Cabecalho
                  campo="ordem"
                  label="#"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="codigo_boleto"
                  label="Boleto"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="nome_cliente"
                  label="Cliente"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="mapa_qd"
                  label="QD"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="mapa_lt"
                  label="LT"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="desde_vencimento"
                  label="Desde Vencimento"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="valor"
                  label="Valor"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="nome_familia"
                  label="Empreendimento"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="descricao_cidade"
                  label="Praça"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <Cabecalho
                  campo="cobrador_atual_nome"
                  label="Cobrador Atual"
                  sort={sort}
                  dir={dir}
                  onSort={trocarSort}
                />
                <th>Cobrador a Atribuir</th>
                <th style={{ width: 70 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {boletosOrdenados.length === 0 && (
                <tr>
                  <td colSpan={13} className="text-center text-muted py-3">
                    Nenhum boleto elegível para os filtros selecionados.
                  </td>
                </tr>
              )}
              {boletosOrdenados.map((b) => {
                const marcado = selecionados.includes(b.codigo_boleto);
                const cobradorAtrib = atribuicoes[b.codigo_boleto];
                const nomeCobrador =
                  cobradorAtrib != null && cobradoresMap
                    ? cobradoresMap.get(cobradorAtrib)
                    : null;
                return (
                  <tr key={`bol-${b.codigo_boleto}`}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        aria-label={`Selecionar boleto ${b.codigo_boleto}`}
                        checked={marcado}
                        onChange={() => onToggleSelecao(b.codigo_boleto)}
                      />
                    </td>
                    <td>{b.ordem}</td>
                    <td>{b.codigo_boleto}</td>
                    <td>{b.nome_cliente}</td>
                    <td>{b.mapa_qd}</td>
                    <td>{b.mapa_lt}</td>
                    <td>
                      {b.desde_vencimento
                        ? new Date(b.desde_vencimento).toLocaleDateString('pt-BR')
                        : '-'}
                    </td>
                    <td className="text-right">{formatarBRL(b.valor)}</td>
                    <td>{b.nome_familia}</td>
                    <td>{b.descricao_cidade}</td>
                    <td>{b.cobrador_atual_nome || '-'}</td>
                    <td>
                      {cobradorAtrib != null ? (
                        <span>
                          <Badge bg="success">
                            {nomeCobrador || `Cobrador ${cobradorAtrib}`}
                          </Badge>
                        </span>
                      ) : b.cobrador_sugerido ? (
                        <small className="text-muted">
                          Sugerido: {b.cobrador_sugerido}
                        </small>
                      ) : (
                        <small className="text-muted">—</small>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-primary"
                        title="Atribuir cobrador"
                        onClick={() => onAbrirModalAtribuir([b.codigo_boleto])}
                      >
                        <FaUserPlus />
                      </Button>{' '}
                      {cobradorAtrib != null && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          title="Remover atribuição"
                          onClick={() => onRemoverAtribuicao(b.codigo_boleto)}
                        >
                          <FaTimes />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      </Card.Body>
      {totalPaginas > 1 && (
        <Card.Footer className="d-flex justify-content-center">
          <Pagination size="sm" className="mb-0">
            {renderizarPaginacao(pagina, totalPaginas, onMudarPagina)}
          </Pagination>
        </Card.Footer>
      )}
    </Card>
  );
};

export default GridBoletos;
